import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ProfileUser, UserSecurityActivity} from "../../api/entity/profile.users";
import {
    CustomerProfileUsersService
} from "../../../modules/customers/shared/api/service/customer-profile-users.service";
import {NotificationManager} from "../../../../../common/providers/notification";
import {TranslateManager} from "../../../../../common/translate";
import {InjectOptionDialog, OptionDialog} from "../../option-dialog";
import {CUSTOMER_USERS_TAB_ROUTE} from "../../../core/security";

const DETAILS_SHOW_KEY = 'CUSTOMER.DETAILS.TAB.USERS.USER_RELATED_ROLE_DETAILS.SHOW_ROLE_DETAILS';
const DETAILS_HIDE_KEY = 'CUSTOMER.DETAILS.TAB.USERS.USER_RELATED_ROLE_DETAILS.HIDE_ROLE_DETAILS';
const KEYS = 'CUSTOMER.DETAILS.TAB.USERS.PROFILE_USER_DETAILS.';
// Enabling 2FA goes through a queue: the authenticator needs a moment before the new state can be read
const TWO_FA_REFRESH_DELAY_MS = 3000;

@Component({
    selector: 'tfl-profile-user-details',
    template: require('./profile-user.details.template.html')
})
export class ProfileUserDetailsComponent implements OnInit {
    readonly usersTabRoute = CUSTOMER_USERS_TAB_ROUTE;

    @Input('profile-user-id') profileUserId: number;
    @Input('profile-id') profileId: number;
    @Input('profile-user-role-id') profileUserRoleId: number;
    // The users list shows the role too: tell it when it changes (own role, make/remove administrator)
    @Output() roleChanged = new EventEmitter<string>();

    profileUser: ProfileUser;
    loadError: string = null;
    twoFaBusy = false;
    adminBusy = false;
    selectedProfileUser: any | null = null;
    securityActivity: UserSecurityActivity | null = null;
    showSecurityActivity = false;
    loadingSecurityActivity = false;

    phoneEditMode = false;
    phoneValue = '';
    savingPhone = false;
    phoneError: string | null = null;
    phoneSaved = false;

    @InjectOptionDialog() dialog: OptionDialog;

    constructor(private _customerProfileUsersService: CustomerProfileUsersService,
                private _notificationManager: NotificationManager,
                private _translateManager: TranslateManager) {
    }

    ngOnInit(): void {
        this.showDetails();
    }

    showDetails(reopenRoleDetails: boolean = false): void {
        this.loadError = null;
        this._customerProfileUsersService.getProfileUserDetail(this.profileId, this.profileUserId)
            .subscribe(
                profileUser => {
                    const roleWasOpen = !!(this.selectedProfileUser && this.selectedProfileUser.showDetails);
                    const previousRole = this.profileUser ? this.profileUser.userRelatedRole : undefined;
                    this.profileUser = profileUser;
                    if (previousRole !== undefined && previousRole !== profileUser.userRelatedRole) {
                        this.roleChanged.emit(profileUser.userRelatedRole);
                    }
                    this.selectedProfileUser = null;
                    if ((roleWasOpen || reopenRoleDetails) && profileUser.userRelatedRoleId) {
                        this.toggleRoleDetails(profileUser);
                    }
                },
                error => this.loadError = this.errorMessage(error)
            );
    }

    onOwnRoleCreated(): void {
        // Close the role section first so it is rebuilt with the new role once the user is reloaded
        this.selectedProfileUser = null;
        this.showDetails(true);
    }

    get isAdministrator(): boolean {
        return !!this.profileUser && (this.profileUser.userRelatedRole || '').toUpperCase() === 'ADMIN_ROLE';
    }

    changeAdministrator(): void {
        if (this.adminBusy || !this.profileUser) {
            return;
        }
        const removing = this.isAdministrator;
        const dialogKey = KEYS + (removing ? 'REMOVE_ADMIN_DIALOG.MESSAGE' : 'MAKE_ADMIN_DIALOG.MESSAGE');
        this.dialog
            .showConfirmDialog({
                title: KEYS + 'ADMIN_DIALOG_TITLE',
                descriptionLines: [{key: dialogKey, params: {user: this.profileUser.userName, id: this.profileUser.id}}],
                confirmButtonText: KEYS + (removing ? 'REMOVE_ADMIN' : 'MAKE_ADMIN'),
                cancelButtonText: 'CUSTOMER.DETAILS.TAB.USERS.CANCEL',
                confirmButtonClass: removing ? 'btn-danger' : 'btn-primary'
            })
            .onConfirmAction(() => {
                this.adminBusy = true;
                const request = removing
                    ? this._customerProfileUsersService.removeAdministrator(this.profileId, this.profileUserId)
                    : this._customerProfileUsersService.makeAdministrator(this.profileId, this.profileUserId);
                request.subscribe(
                    response => {
                        this.adminBusy = false;
                        this._notificationManager.success(response && response.message, 5000);
                        this.onOwnRoleCreated();
                    },
                    error => {
                        this.adminBusy = false;
                        this._notificationManager.error(this.errorMessage(error));
                    }
                );
            });
    }

    toggleRoleDetails(profileUser: any): void {
        if (this.selectedProfileUser && this.selectedProfileUser.id === profileUser.id) {
            this.selectedProfileUser.showDetails = !this.selectedProfileUser.showDetails;
        } else {
            if (this.selectedProfileUser) {
                this.selectedProfileUser.showDetails = false;
            }
            profileUser.showDetails = true;
            this.selectedProfileUser = profileUser;
        }
    }

    getActionProfileUserRoleDetailText(showDetails: boolean): string {
        return showDetails ? DETAILS_HIDE_KEY : DETAILS_SHOW_KEY;
    }

    active2FA(profileUserId: number): void {
        this.twoFaBusy = true;
        this._customerProfileUsersService.active2FA(profileUserId)
            .subscribe(
                () => {
                    this._notificationManager.success(this._translateManager.getValue(KEYS + 'TWO_FA_REQUESTED'), 5000);
                    this.afterTwoFaChange(TWO_FA_REFRESH_DELAY_MS);
                },
                error => this.onTwoFaError(error)
            );
    }

    disable2FA(profileUserId: number): void {
        this.confirmTwoFaChange(KEYS + 'DISABLE_2FA_DIALOG.MESSAGE', KEYS + 'DISABLE_2FA', () =>
            this._customerProfileUsersService.disable2FA(profileUserId)
                .subscribe(() => this.afterTwoFaChange(0), error => this.onTwoFaError(error)));
    }

    disableTotp(profileUserId: number): void {
        this.confirmTwoFaChange(KEYS + 'DISABLE_TOTP_DIALOG.MESSAGE', KEYS + 'DISABLE_TOTP', () =>
            this._customerProfileUsersService.disableTotp(profileUserId)
                .subscribe(() => this.afterTwoFaChange(0), error => this.onTwoFaError(error)));
    }

    private confirmTwoFaChange(messageKey: string, confirmKey: string, action: () => void): void {
        this.dialog
            .showConfirmDialog({
                title: KEYS + 'TWO_FA_DIALOG_TITLE',
                descriptionLines: [{key: messageKey, params: {user: this.profileUser && this.profileUser.userName}}],
                confirmButtonText: confirmKey,
                cancelButtonText: 'CUSTOMER.DETAILS.TAB.USERS.CANCEL',
                confirmButtonClass: 'btn-danger'
            })
            .onConfirmAction(() => {
                this.twoFaBusy = true;
                action();
            });
    }

    private afterTwoFaChange(delayMs: number): void {
        // The security panel is cached: drop it so it shows the new state next time it is opened
        this.securityActivity = null;
        this.showSecurityActivity = false;
        setTimeout(() => {
            this.twoFaBusy = false;
            this.showDetails();
        }, delayMs);
    }

    private onTwoFaError(error: any): void {
        this.twoFaBusy = false;
        this._notificationManager.error(this.errorMessage(error));
    }

    private errorMessage(error: any): string {
        return (error && error.message) || this._translateManager.getValue(KEYS + 'ACTION_ERROR');
    }

    get2FATypeLabel(): string {
        if (!this.profileUser) { return this._translateManager.getValue(KEYS + 'NONE'); }
        const types: string[] = [];
        if (this.profileUser.totpEnabled) { types.push('TOTP (App)'); }
        if (this.profileUser.emailSmsEnabled) { types.push('Email + SMS'); }
        return types.length > 0 ? types.join(', ') : this._translateManager.getValue(KEYS + 'NONE');
    }

    toggleSecurityActivity(): void {
        if (this.showSecurityActivity) {
            this.showSecurityActivity = false;
            return;
        }
        if (this.securityActivity) {
            this.showSecurityActivity = true;
            return;
        }
        this.loadingSecurityActivity = true;
        this._customerProfileUsersService.getSecurityActivity(this.profileId, this.profileUserId)
            .subscribe(
                data => {
                    this.securityActivity = data;
                    this.showSecurityActivity = true;
                    this.loadingSecurityActivity = false;
                },
                error => {
                    this.loadingSecurityActivity = false;
                    this._notificationManager.error(this.errorMessage(error));
                }
            );
    }

    startPhoneEdit(): void {
        this.phoneValue = '';
        this.phoneError = null;
        this.phoneSaved = false;
        this.phoneEditMode = true;
    }

    cancelPhoneEdit(): void {
        this.phoneEditMode = false;
        this.phoneError = null;
    }

    savePhone(): void {
        if (!this.phoneValue || !this.phoneValue.trim()) {
            this.phoneError = this._translateManager.getValue(KEYS + 'PHONE_EMPTY');
            return;
        }
        this.savingPhone = true;
        this.phoneError = null;
        this._customerProfileUsersService.updateUserPhone(this.profileId, this.profileUserId, this.phoneValue.trim())
            .subscribe(
                () => {
                    this.savingPhone = false;
                    this.phoneEditMode = false;
                    this.phoneSaved = true;
                    // Update the displayed phone in the security panel
                    if (this.securityActivity) {
                        this.securityActivity.currentPhone = this.phoneValue.trim();
                    }
                },
                error => {
                    this.savingPhone = false;
                    this.phoneError = (error && error.message) || this._translateManager.getValue(KEYS + 'PHONE_SAVE_ERROR');
                }
            );
    }

    getLastLogin(): any | null {
        if (!this.securityActivity || !this.securityActivity.loginHistory) { return null; }
        for (var i = 0; i < this.securityActivity.loginHistory.length; i++) {
            if (this.securityActivity.loginHistory[i].actionCode === 'LOG_IN') {
                return this.securityActivity.loginHistory[i];
            }
        }
        return null;
    }

    getActionCodeClass(code: string): string {
        switch (code) {
            case 'LOG_IN': return 'label-success';
            case 'LOG_OUT': return 'label-default';
            case 'LOG_IN_FAILED': return 'label-warning';
            case 'LOG_IN_BLOCKED': return 'label-danger';
            case 'TWO_FA_SUCCESS': return 'label-info';
            case 'TWO_FA_FAILED': return 'label-danger';
            default: return 'label-default';
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/shared/profile-user/profile-user-details/profile-user.details.component.ts